import {
  afterNextRender,
  Component,
  computed,
  DestroyRef,
  effect,
  ElementRef,
  EventEmitter,
  inject,
  input,
  model,
  Output,
  signal,
  untracked,
  viewChild,
} from "@angular/core";
import * as d3 from "d3";

import { GatherMeta, GatherSamples } from "../../../../models/segy";

/** Space reserved around the plot area for the D3 axes and the meta caption. */
const MARGIN = { top: 20, right: 14, bottom: 40, left: 50 } as const;

/** Height (px) of one header track drawn above the gather, and the gap
 * between stacked tracks. */
const TRACK_HEIGHT = 58;
const TRACK_GAP = 8;
const TRACK_COLORS = ["#1890ff", "#fa8c16", "#52c41a", "#722ed1", "#eb2f96"];

/** A trace-header field plotted above the gather, one value per trace of the
 * displayed gather (in gather order). */
export interface GatherHeaderTrack {
  name: string;
  label: string;
  values: number[];
}

/** A mute curve drawn over the gather: one time (s) per trace of the
 * displayed gather (null where it can't be computed). */
export interface GatherMuteLine {
  label: string;
  color: string;
  times: (number | null)[];
  /** drawn dashed (e.g. auto-picked curves next to the fitted lines) */
  dashed?: boolean;
}

/** How the gather is drawn: the server-rendered PNG (smoothed), or
 * client-side from the samples as crisp per-sample cells or wiggle traces. */
export type GatherDisplayMode = "image" | "cells" | "wiggle";

/** Wiggles closer than this (CSS px) are thinned to every n-th trace. */
const MIN_WIGGLE_SPACING = 3;

/**
 * Box-filter weights mapping source cells [lo, hi) (fractional cell units,
 * `nSrc` cells in total) onto `n` output pixels: pixel p takes cell k with
 * the fraction of its footprint that k covers. Entries of pixel p are
 * `start[p] .. start[p + 1]`; footprint outside the cells is left unweighted.
 */
function areaWeights(
  lo: number,
  hi: number,
  n: number,
  nSrc: number,
): { start: Int32Array; k: Int32Array; w: Float32Array } {
  const step = (hi - lo) / n;
  const start = new Int32Array(n + 1);
  const ks: number[] = [];
  const ws: number[] = [];
  for (let p = 0; p < n; p++) {
    start[p] = ks.length;
    const a = lo + p * step;
    const b = a + step;
    const kEnd = Math.min(nSrc, Math.ceil(b));
    for (let k = Math.max(0, Math.floor(a)); k < kEnd; k++) {
      const overlap = Math.min(b, k + 1) - Math.max(a, k);
      if (overlap > 0) {
        ks.push(k);
        ws.push(overlap / step);
      }
    }
  }
  start[n] = ks.length;
  return { start, k: Int32Array.from(ks), w: Float32Array.from(ws) };
}

/** Steffen (1990) slope at the middle of three unit-spaced values: zero at
 * a local extremum and limited so the Hermite cubic stays within each pair
 * of neighbouring samples (no overshoot). */
function steffenSlope(y0: number, y1: number, y2: number): number {
  const d0 = y1 - y0;
  const d1 = y2 - y1;
  if (d0 * d1 <= 0) return 0;
  const p = 0.5 * (d0 + d1);
  return (Math.sign(d0) + Math.sign(d1)) *
    Math.min(Math.abs(d0), Math.abs(d1), 0.5 * Math.abs(p));
}

/** Start of a window of width `span` kept inside [0, 1]. */
function clamp01(start: number, span: number): number {
  return Math.max(0, Math.min(1 - span, start));
}

/** Minimum drag distance (px) before a press is treated as a zoom box rather
 * than a trace-selecting click. */
const DRAG_THRESHOLD = 4;

/**
 * A zoom window expressed as fractions [0,1] of the full trace (x) and time
 * (y) domains. Being resolution-independent it can be shared verbatim between
 * the two compare panels (whose images may differ in pixel size). null = the
 * full, unzoomed view.
 */
export interface GatherZoom {
  x0: number;
  x1: number;
  y0: number;
  y1: number;
}

/**
 * Presentational shot-gather viewer: stretches a server-rendered base64 PNG
 * into the plot area and draws D3 axes around it from the gather meta
 * (x: trace index, y: time in seconds), following the spirit of
 * shot-image/d3-image. Clicking the image inverts the x scale to a trace
 * index and emits `traceClick`; the selected trace is highlighted with a
 * vertical line.
 *
 * Drag a rubber-band box over the image to zoom into a trace/time window —
 * the image is enlarged and clipped to that window and the axes redraw to the
 * zoomed domain, so the same region can be compared before/after filtering.
 * Zoom is a two-way `model` so callers (the compare view) can sync the window
 * across panels; left at its default it is managed internally.
 */
@Component({
  selector: "app-segy-gather-viewer",
  templateUrl: "./gather-viewer.component.html",
  styleUrl: "./gather-viewer.component.less",
  standalone: false,
  host: { "[style.--tracks-height]": "tracksHeight() + 'px'" },
})
export class GatherViewerComponent {
  // ─────────────────────────────────────────────────────────────
  // INPUTS / OUTPUTS
  // ─────────────────────────────────────────────────────────────

  /** Base64-encoded PNG of the gather (without the data-URL prefix). */
  public readonly image = input<string | null>(null);
  /** Render meta returned alongside the image; drives the axes. */
  public readonly meta = input<GatherMeta | null>(null);
  public readonly loading = input<boolean>(false);
  /** Trace highlighted with a vertical marker line (null = none). */
  public readonly selectedTraceIndex = input<number | null>(null);
  /** Header values plotted as stacked tracks above the gather, sharing its
   * trace axis (and zoom) so each value lines up with its trace. */
  public readonly headerTracks = input<GatherHeaderTrack[]>([]);
  /** Mute curves overlaid on the gather (time against trace). */
  public readonly mutes = input<GatherMuteLine[]>([]);
  /** Image (server PNG) or client-drawn cells / wiggles from `samples`. */
  public readonly displayMode = input<GatherDisplayMode>("image");
  /** Samples for the cells / wiggle modes (ignored in image mode). */
  public readonly samples = input<GatherSamples | null>(null);
  /** Wiggle excursion, in trace spacings at the clip amplitude. */
  public readonly wiggleGain = input<number>(1);
  /** Draw every n-th trace as a wiggle; null = auto (spacing-based). */
  public readonly wiggleStep = input<number | null>(null);

  /** Current zoom window (two-way). Left at the default `null` for the single
   * viewer; the compare view binds both panels to one signal so a zoom in
   * either pane frames the same trace/time window in both. */
  public readonly zoom = model<GatherZoom | null>(null);

  /** Emits the (original, undecimated) trace index that was clicked. */
  @Output() traceClick = new EventEmitter<number>();
  /** Emits the wiggle trace step actually drawn (auto or overridden). */
  @Output() wiggleStrideChange = new EventEmitter<number>();

  // ─────────────────────────────────────────────────────────────
  // VIEW STATE
  // ─────────────────────────────────────────────────────────────

  private container = viewChild<ElementRef<HTMLDivElement>>("container");
  private axesSvg = viewChild<ElementRef<SVGSVGElement>>("axes");
  private overlaySvg = viewChild<ElementRef<SVGSVGElement>>("overlay");
  private canvas = viewChild<ElementRef<HTMLCanvasElement>>("canvas");


  /** Total height (px) taken by the header tracks above the plot area. */
  protected readonly tracksHeight = computed<number>(
    () => this.headerTracks().length * (TRACK_HEIGHT + TRACK_GAP),
  );

  /** Trace under the cursor (0-based ordinal), for the track readouts. */
  private readonly hoverTrace = signal<number | null>(null);

  /** Measured container size, kept current by a ResizeObserver. */
  private containerSize = signal<{ width: number; height: number } | null>(
    null,
  );

  /** Previous zoom windows, so "Back" steps out one level at a time. */
  protected readonly zoomHistory = signal<(GatherZoom | null)[]>([]);
  /** Drag pans instead of drawing a zoom box (also: Shift / middle-drag). */
  protected readonly panTool = signal<boolean>(false);
  /** In-progress pan drag: where it started and the window at that time. */
  private readonly panStart = signal<{
    x: number;
    y: number;
    zoom: GatherZoom;
    moved: boolean;
  } | null>(null);
  protected readonly panning = computed(() => this.panStart() != null);
  /** Last pinch / ctrl-wheel zoom, to record one history entry per gesture. */
  private lastWheelZoomAt = 0;

  /** In-progress rubber-band drag, in plot-area pixel coords (null = none). */
  private readonly dragStart = signal<{ x: number; y: number } | null>(null);
  private readonly dragCurrent = signal<{ x: number; y: number } | null>(null);

  /** Pixel rectangle of the plot area (the stretched image) in the container. */
  protected readonly plotRect = computed<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(() => {
    const size = this.containerSize();
    if (!size) return null;
    const top = MARGIN.top + this.tracksHeight();
    const width = size.width - MARGIN.left - MARGIN.right;
    const height = size.height - top - MARGIN.bottom;
    if (width < 20 || height < 20) return null;
    return { left: MARGIN.left, top, width, height };
  });

  protected readonly imageSrc = computed<string | null>(() => {
    const image = this.image();
    return image ? `data:image/png;base64,${image}` : null;
  });

  protected readonly isZoomed = computed<boolean>(() => this.zoom() != null);

  protected readonly drawsSamples = computed<boolean>(
    () => this.displayMode() !== "image",
  );

  /** Every n-th trace drawn as a wiggle (1 = all), set by the last draw. */
  protected readonly wiggleStride = signal<number>(1);

  /** Caption warning when not every trace / sample is on screen. */
  protected readonly sampleNote = computed<string | null>(() => {
    const samples = this.samples();
    if (!this.drawsSamples() || !samples) return null;
    const notes: string[] = [];
    if (samples.meta.decim_traces > 1) {
      notes.push(`1 in ${samples.meta.decim_traces} traces (large gather)`);
    }
    if (this.displayMode() === "wiggle" && this.wiggleStride() > 1) {
      notes.push(
        this.wiggleStep()
          ? `wiggles 1 in ${this.wiggleStride()}`
          : `wiggles 1 in ${this.wiggleStride()} — zoom in for all`,
      );
    }
    if (samples.meta.decim_time > 1) {
      notes.push(`time averaged ×${samples.meta.decim_time}`);
    }
    return notes.length ? notes.join(" · ") : null;
  });

  /** Grey level (0–255) of every sample, trace-major like `samples.data`,
   * mapped like the server PNG: vmin black -> vmax white. Built once per
   * sample set; each redraw resamples the zoomed part of it. */
  private readonly cellGreys = computed<Float32Array | null>(() => {
    const samples = this.samples();
    if (!samples || this.displayMode() !== "cells") return null;
    const { vmin, vmax, scale } = samples.meta;
    const span = vmax - vmin;
    const data = samples.data;
    const greys = new Float32Array(data.length);
    for (let i = 0; i < data.length; i++) {
      greys[i] =
        span > 0
          ? Math.min(255, Math.max(0, ((data[i] * scale - vmin) / span) * 256))
          : 128;
    }
    return greys;
  });

  /** Pixel box for the (possibly zoomed) image inside the clipped plot area.
   * Unzoomed it fills the plot rect; zoomed it is enlarged and shifted so the
   * selected fraction fills the rect (the remainder is clipped away by the
   * plot area's overflow:hidden). */
  protected readonly imageBox = computed<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(() => {
    const rect = this.plotRect();
    if (!rect) return null;
    const z = this.zoom();
    if (!z) return { left: 0, top: 0, width: rect.width, height: rect.height };
    const width = rect.width / (z.x1 - z.x0);
    const height = rect.height / (z.y1 - z.y0);
    return { left: -z.x0 * width, top: -z.y0 * height, width, height };
  });

  /** Rubber-band rectangle to draw while dragging (plot-area px), or null. */
  protected readonly selectionRect = computed<{
    left: number;
    top: number;
    width: number;
    height: number;
  } | null>(() => {
    const start = this.dragStart();
    const current = this.dragCurrent();
    if (!start || !current) return null;
    return {
      left: Math.min(start.x, current.x),
      top: Math.min(start.y, current.y),
      width: Math.abs(current.x - start.x),
      height: Math.abs(current.y - start.y),
    };
  });

  private resizeObserver?: ResizeObserver;

  constructor() {
    afterNextRender(() => {
      const el = this.container()?.nativeElement;
      if (!el) return;
      this.resizeObserver = new ResizeObserver((entries) => {
        const rect = entries[0]?.contentRect;
        if (rect && rect.width > 0 && rect.height > 0) {
          this.containerSize.set({ width: rect.width, height: rect.height });
        }
      });
      this.resizeObserver.observe(el);
      this.containerSize.set({
        width: el.clientWidth,
        height: el.clientHeight,
      });
    });
    inject(DestroyRef).onDestroy(() => this.resizeObserver?.disconnect());

    // redraw the axes whenever the meta, container size or selection change
    effect(() => this.drawAxes());
    // cells / wiggles are drawn client-side at screen resolution, so a zoom
    // shows the real samples rather than a stretched bitmap
    effect(() => this.drawSamples());
    // a fresh (null) zoom — new gather or reset — starts a new history
    effect(() => {
      if (this.zoom() == null) untracked(() => this.zoomHistory.set([]));
    });
    // the hover readout lives on its own layer so moving the cursor doesn't
    // redraw every track
    effect(() => this.drawHover());
  }

  // ─────────────────────────────────────────────────────────────
  // INTERACTION
  // ─────────────────────────────────────────────────────────────

  /** Trace/time domains currently in view, accounting for the zoom window. */
  private viewDomain(): {
    traceLo: number;
    traceHi: number;
    timeLo: number;
    timeHi: number;
  } | null {
    const meta = this.meta();
    if (!meta) return null;
    const z = this.zoom();
    const x0 = z?.x0 ?? 0;
    const x1 = z?.x1 ?? 1;
    const y0 = z?.y0 ?? 0;
    const y1 = z?.y1 ?? 1;
    // a time-windowed gather starts at t_min rather than 0
    const tMin = meta.t_min ?? 0;
    const tSpan = meta.t_max - tMin;
    return {
      traceLo: x0 * meta.n_traces,
      traceHi: x1 * meta.n_traces,
      timeLo: tMin + y0 * tSpan,
      timeHi: tMin + y1 * tSpan,
    };
  }

  /** Cursor position relative to the plot area (robust to the enlarged,
   * offset image that sits on top of it while zoomed). */
  private localPoint(event: MouseEvent): { x: number; y: number } {
    const bounds = (
      event.currentTarget as HTMLElement
    ).getBoundingClientRect();
    return { x: event.clientX - bounds.left, y: event.clientY - bounds.top };
  }

  protected onPointerDown(event: MouseEvent): void {
    if (event.button !== 0 && event.button !== 1) return;
    const point = this.localPoint(event);
    const zoom = this.zoom();
    const pan = event.button === 1 || event.shiftKey || this.panTool();
    if (pan && zoom) {
      event.preventDefault(); // no middle-click autoscroll
      this.panStart.set({ ...point, zoom, moved: false });
      return;
    }
    if (event.button !== 0) return;
    this.dragStart.set(point);
    this.dragCurrent.set(point);
  }

  protected onPointerMove(event: MouseEvent): void {
    const point = this.localPoint(event);
    this.hoverTrace.set(this.traceAt(point.x));
    const pan = this.panStart();
    if (pan) {
      this.panBy(pan.zoom, pan.x - point.x, pan.y - point.y);
      if (
        Math.abs(point.x - pan.x) >= DRAG_THRESHOLD ||
        Math.abs(point.y - pan.y) >= DRAG_THRESHOLD
      ) {
        this.panStart.set({ ...pan, moved: true });
      }
      return;
    }
    if (!this.dragStart()) return;
    this.dragCurrent.set(point);
  }

  protected onPointerUp(event: MouseEvent): void {
    const pan = this.panStart();
    if (pan) {
      this.panStart.set(null);
      // a click without a drag still selects the trace
      if (!pan.moved && event.button === 0) this.selectTraceAt(pan.x);
      return;
    }
    const start = this.dragStart();
    if (!start) return;
    const end = this.localPoint(event);
    this.dragStart.set(null);
    this.dragCurrent.set(null);

    // a press that barely moved is a trace-selecting click; a real drag zooms
    if (
      Math.abs(end.x - start.x) < DRAG_THRESHOLD &&
      Math.abs(end.y - start.y) < DRAG_THRESHOLD
    ) {
      this.selectTraceAt(start.x);
    } else {
      this.zoomToBox(start, end);
    }
  }

  /** Abandon an in-progress drag if the cursor leaves the plot area. */
  protected onPointerLeave(): void {
    this.hoverTrace.set(null);
    this.panStart.set(null);
    this.dragStart.set(null);
    this.dragCurrent.set(null);
  }

  /** Map a plot-area x to a trace index via the (zoomed) x-scale invert. */
  private traceAt(px: number): number | null {
    const meta = this.meta();
    const rect = this.plotRect();
    const dom = this.viewDomain();
    if (!meta || !rect || !dom || !(meta.n_traces > 0)) return null;
    const xScale = d3
      .scaleLinear()
      .domain([dom.traceLo, dom.traceHi])
      .range([0, rect.width]);
    const index = Math.floor(xScale.invert(px));
    return Math.max(0, Math.min(meta.n_traces - 1, index));
  }

  private selectTraceAt(px: number): void {
    const index = this.traceAt(px);
    if (index != null) this.traceClick.emit(index);
  }

  /** Compose a pixel selection within the current view into a new fractional
   * zoom window (so successive zooms nest correctly). */
  private zoomToBox(
    a: { x: number; y: number },
    b: { x: number; y: number },
  ): void {
    const rect = this.plotRect();
    if (!rect) return;
    const left = Math.max(0, Math.min(a.x, b.x));
    const right = Math.min(rect.width, Math.max(a.x, b.x));
    const top = Math.max(0, Math.min(a.y, b.y));
    const bottom = Math.min(rect.height, Math.max(a.y, b.y));
    if (right - left < DRAG_THRESHOLD || bottom - top < DRAG_THRESHOLD) return;

    const z = this.zoom() ?? { x0: 0, x1: 1, y0: 0, y1: 1 };
    const spanX = z.x1 - z.x0;
    const spanY = z.y1 - z.y0;
    this.pushHistory();
    this.zoom.set({
      x0: z.x0 + (left / rect.width) * spanX,
      x1: z.x0 + (right / rect.width) * spanX,
      y0: z.y0 + (top / rect.height) * spanY,
      y1: z.y0 + (bottom / rect.height) * spanY,
    });
  }

  protected resetZoom(): void {
    this.zoomHistory.set([]);
    this.zoom.set(null);
  }

  /** Step back out to the previous zoom window. */
  protected zoomBack(): void {
    const history = this.zoomHistory();
    if (!history.length) return this.resetZoom();
    this.zoom.set(history[history.length - 1]);
    this.zoomHistory.set(history.slice(0, -1));
  }

  private pushHistory(): void {
    this.zoomHistory.update((h) => [...h, this.zoom()].slice(-50));
  }

  /** Shift the window `from` by a pixel offset (content follows the drag). */
  private panBy(from: GatherZoom, dxPx: number, dyPx: number): void {
    const rect = this.plotRect();
    if (!rect) return;
    const spanX = from.x1 - from.x0;
    const spanY = from.y1 - from.y0;
    const x0 = clamp01(from.x0 + (dxPx / rect.width) * spanX, spanX);
    const y0 = clamp01(from.y0 + (dyPx / rect.height) * spanY, spanY);
    this.zoom.set({ x0, x1: x0 + spanX, y0, y1: y0 + spanY });
  }

  /** Trackpad / wheel: scroll pans the zoomed view (Shift scrolls across the
   * traces); pinch or Ctrl/⌘ + wheel zooms around the cursor. */
  protected onWheel(event: WheelEvent): void {
    const rect = this.plotRect();
    if (!rect) return;
    const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? rect.height : 1;
    let dx = event.deltaX * unit;
    let dy = event.deltaY * unit;

    if (event.ctrlKey || event.metaKey) {
      event.preventDefault();
      const now = Date.now();
      if (now - this.lastWheelZoomAt > 400) this.pushHistory();
      this.lastWheelZoomAt = now;
      const p = this.localPoint(event);
      this.zoomAround(p.x / rect.width, p.y / rect.height, Math.exp(dy * 0.01));
      return;
    }
    const z = this.zoom();
    if (!z) return; // unzoomed: let the page scroll
    event.preventDefault();
    if (event.shiftKey && !dx) [dx, dy] = [dy, 0];
    this.panBy(z, dx, dy);
  }

  /** Scale the window by `factor` (< 1 zooms in) about a point given as
   * fractions of the plot area. */
  private zoomAround(fx: number, fy: number, factor: number): void {
    const meta = this.meta();
    const z = this.zoom() ?? { x0: 0, x1: 1, y0: 0, y1: 1 };
    // don't zoom past ~2 traces / 2 samples across the view
    const minX = Math.min(1, 2 / (meta?.n_traces || 2));
    const minY = Math.min(1, 2 / (meta?.ns || 2));
    const spanX = Math.min(1, Math.max(minX, (z.x1 - z.x0) * factor));
    const spanY = Math.min(1, Math.max(minY, (z.y1 - z.y0) * factor));
    if (spanX >= 1 && spanY >= 1) {
      this.zoom.set(null);
      return;
    }
    const cx = z.x0 + fx * (z.x1 - z.x0);
    const cy = z.y0 + fy * (z.y1 - z.y0);
    const x0 = clamp01(cx - fx * spanX, spanX);
    const y0 = clamp01(cy - fy * spanY, spanY);
    this.zoom.set({ x0, x1: x0 + spanX, y0, y1: y0 + spanY });
  }

  // ─────────────────────────────────────────────────────────────
  // D3 AXES
  // ─────────────────────────────────────────────────────────────

  private drawAxes(): void {
    const svgEl = this.axesSvg()?.nativeElement;
    if (!svgEl) return;

    const svg = d3.select(svgEl);
    svg.selectAll("*").remove();

    const meta = this.meta();
    const rect = this.plotRect();
    const dom = this.viewDomain();
    if (!meta || !rect || !dom) return;

    const xScale = d3
      .scaleLinear()
      .domain([dom.traceLo, dom.traceHi])
      .range([0, rect.width]);
    const yScale = d3
      .scaleLinear()
      .domain([dom.timeLo, dom.timeHi])
      .range([0, rect.height]);

    const g = svg
      .append("g")
      .attr("transform", `translate(${rect.left}, ${rect.top})`);

    g.append("g")
      .attr("transform", `translate(0, ${rect.height})`)
      .call(
        d3
          .axisBottom(xScale)
          .ticks(Math.min(10, Math.max(2, meta.n_traces)))
          .tickSizeOuter(0),
      );
    g.append("g").call(d3.axisLeft(yScale).ticks(8).tickSizeOuter(0));

    // axis labels (styled inline: d3-created nodes don't get the emulated
    // view-encapsulation attribute, so component CSS can't reach them)
    g.append("text")
      .attr("x", rect.width / 2)
      .attr("y", rect.height + 32)
      .attr("text-anchor", "middle")
      .attr("fill", "#666")
      .style("font-size", "11px")
      .text("Trace");
    g.append("text")
      .attr("transform", "rotate(-90)")
      .attr("x", -rect.height / 2)
      .attr("y", -36)
      .attr("text-anchor", "middle")
      .attr("fill", "#666")
      .style("font-size", "11px")
      .text("Time (s)");

    this.drawTracks(g, xScale, rect, dom, meta.n_traces);
    this.drawMutes(g, xScale, yScale, rect, meta.n_traces);

    // vertical highlight at the selected trace (centred on its column),
    // drawn only when it falls inside the current (zoomed) view; it runs up
    // through the header tracks so the trace's values are easy to read off
    const selected = this.selectedTraceIndex();
    if (
      selected != null &&
      selected + 0.5 >= dom.traceLo &&
      selected + 0.5 <= dom.traceHi
    ) {
      g.append("line")
        .attr("x1", xScale(selected + 0.5))
        .attr("x2", xScale(selected + 0.5))
        .attr("y1", -this.tracksHeight())
        .attr("y2", rect.height)
        .attr("stroke", "#ff4d4f")
        .attr("stroke-width", 1.5)
        .attr("stroke-dasharray", "4,3")
        .attr("pointer-events", "none");
    }
  }

  /** Stacked header tracks above the plot area: one line per field over the
   * traces in view, each scaled to its own visible range. */
  private drawTracks(
    g: d3.Selection<SVGGElement, unknown, null, undefined>,
    xScale: d3.ScaleLinear<number, number>,
    rect: { width: number },
    dom: { traceLo: number; traceHi: number },
    nTraces: number,
  ): void {
    const tracks = this.headerTracks();
    if (!tracks.length) return;

    tracks.forEach((track, k) => {
      const top = -this.tracksHeight() + k * (TRACK_HEIGHT + TRACK_GAP);
      const color = TRACK_COLORS[k % TRACK_COLORS.length];
      // header rows map onto the gather's traces (normally 1:1)
      const ratio = nTraces / (track.values.length || 1);
      const points: [number, number][] = [];
      let lo = Infinity;
      let hi = -Infinity;
      track.values.forEach((v, i) => {
        const x = (i + 0.5) * ratio;
        if (!isFinite(v)) return;
        points.push([x, v]);
        if (x >= dom.traceLo && x <= dom.traceHi) {
          if (v < lo) lo = v;
          if (v > hi) hi = v;
        }
      });
      if (!isFinite(lo)) {
        lo = 0;
        hi = 1;
      } else if (lo === hi) {
        const pad = Math.abs(lo) * 0.05 || 1;
        lo -= pad;
        hi += pad;
      }
      const yScale = d3
        .scaleLinear()
        .domain([lo, hi])
        .range([top + TRACK_HEIGHT - 3, top + 3]);

      const t = g.append("g");
      t.append("rect")
        .attr("x", 0)
        .attr("y", top)
        .attr("width", rect.width)
        .attr("height", TRACK_HEIGHT)
        .attr("fill", "#fafafa")
        .attr("stroke", "#f0f0f0");
      t.append("g").call(
        d3
          .axisLeft(yScale)
          .ticks(3)
          .tickSize(3)
          .tickSizeOuter(0)
          .tickFormat((v) => this.formatTick(+v)),
      )
        .call((a) => a.selectAll("text").style("font-size", "9px"))
        .call((a) => a.select(".domain").attr("stroke", "#d9d9d9"));

      // nested viewport clips the line to the track (see drawMutes)
      const plot = t
        .append("svg")
        .attr("y", top)
        .attr("width", rect.width)
        .attr("height", TRACK_HEIGHT)
        .attr("viewBox", `0 ${top} ${rect.width} ${TRACK_HEIGHT}`)
        .style("overflow", "hidden");
      plot
        .append("path")
        .datum(points)
        .attr("fill", "none")
        .attr("stroke", color)
        .attr("stroke-width", 1.4)
        .attr(
          "d",
          d3
            .line<[number, number]>()
            .x((p) => xScale(p[0]))
            .y((p) => yScale(p[1])),
        );
      // individual values stay visible while the traces are well spaced
      const visible = (dom.traceHi - dom.traceLo) / (ratio || 1);
      if (visible <= rect.width / 4) {
        plot
          .selectAll("circle")
          .data(points)
          .join("circle")
          .attr("cx", (p) => xScale(p[0]))
          .attr("cy", (p) => yScale(p[1]))
          .attr("r", 1.8)
          .attr("fill", color);
      }

      t.append("text")
        .attr("x", 6)
        .attr("y", top + 11)
        .attr("fill", color)
        .style("font-size", "10px")
        .style("font-weight", "600")
        .text(track.label);
    });
  }

  /** Mute curves over the plot area (clipped to it, so zoom just works). */
  private drawMutes(
    g: d3.Selection<SVGGElement, unknown, null, undefined>,
    xScale: d3.ScaleLinear<number, number>,
    yScale: d3.ScaleLinear<number, number>,
    rect: { width: number; height: number },
    nTraces: number,
  ): void {
    const mutes = this.mutes().filter((m) => m.times.length);
    if (!mutes.length) return;
    // a nested <svg> viewport clips to the plot area by itself — a
    // url(#id) clip-path can fail to resolve under <base href>, letting
    // curves that leave the time window spill over the header tracks
    const layer = g
      .append("svg")
      .attr("width", rect.width)
      .attr("height", rect.height)
      .style("overflow", "hidden")
      .attr("pointer-events", "none");
    for (const mute of mutes) {
      const ratio = nTraces / mute.times.length;
      const line = d3
        .line<number | null>()
        .defined((t) => t != null && isFinite(t))
        .x((_, i) => xScale((i + 0.5) * ratio))
        .y((t) => yScale(t!));
      // white halo keeps the line readable over dense wiggles
      layer
        .append("path")
        .datum(mute.times)
        .attr("fill", "none")
        .attr("stroke", "rgba(255,255,255,0.8)")
        .attr("stroke-width", 4)
        .attr("d", line);
      layer
        .append("path")
        .datum(mute.times)
        .attr("fill", "none")
        .attr("stroke", mute.color)
        .attr("stroke-width", 2)
        .attr("stroke-dasharray", mute.dashed ? "6,4" : null)
        .attr("d", line);
    }
  }

  // ─────────────────────────────────────────────────────────────
  // CELLS / WIGGLES (client-side, from the samples)
  // ─────────────────────────────────────────────────────────────

  /** Draw the zoomed window of the samples into the plot-area canvas at
   * device resolution, as cells (one rectangle per sample) or wiggles. */
  private drawSamples(): void {
    const el = this.canvas()?.nativeElement;
    const samples = this.samples();
    const rect = this.plotRect();
    const dom = this.viewDomain();
    const mode = this.displayMode();
    const greys = this.cellGreys();
    const gain = this.wiggleGain();
    const step = this.wiggleStep();
    if (!el || !rect || mode === "image") return;

    const dpr = window.devicePixelRatio || 1;
    el.width = Math.round(rect.width * dpr);
    el.height = Math.round(rect.height * dpr);
    const ctx = el.getContext("2d");
    if (!ctx) return;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.fillStyle = "#fff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    if (!samples || !dom) return;

    const { width: nCols, height: nRows, decim_traces: dk } = samples.meta;
    const tMin = samples.meta.t_min ?? 0;
    const tSpan = samples.meta.t_max - tMin || 1;
    // the view as fractional sample columns / rows (column j = trace j·dk;
    // rows spread evenly over [t_min, t_max], as in the PNG)
    const c0 = dom.traceLo / dk;
    const c1 = dom.traceHi / dk;
    const r0 = ((dom.timeLo - tMin) / tSpan) * nRows;
    const r1 = ((dom.timeHi - tMin) / tSpan) * nRows;
    const colPx = rect.width / (c1 - c0);
    const rowPx = rect.height / (r1 - r0);

    if (mode === "cells") {
      if (greys) this.drawCells(ctx, el, greys, nCols, nRows, c0, c1, r0, r1);
      return;
    }

    // wiggles: variable area (positive lobes filled) + the trace line
    const stride =
      step && step >= 1
        ? Math.round(step)
        : Math.max(1, Math.ceil(MIN_WIGGLE_SPACING / colPx));
    if (untracked(this.wiggleStride) !== stride) {
      this.wiggleStride.set(stride);
      this.wiggleStrideChange.emit(stride);
    }
    const spacing = colPx * stride;
    const clip = Math.max(Math.abs(samples.meta.vmin), Math.abs(samples.meta.vmax));
    const pxPerAmp = clip > 0 ? (gain * spacing * samples.meta.scale) / clip : 0;
    // amplitudes clip at the display clip, as in the image: a spike reaches
    // the neighbouring trace's baseline at most (gain 1) instead of shooting
    // across the plot when deeply zoomed
    const maxExcursion = gain * spacing;
    const jFirst = Math.max(0, Math.floor(c0 / stride) * stride);
    const jLast = Math.min(nCols - 1, Math.ceil(c1));
    const rFirst = Math.max(0, Math.floor(r0) - 1);
    const rLast = Math.min(nRows - 1, Math.ceil(r1) + 1);
    // no point drawing more than ~2 vertices per device pixel down a trace
    const rStep = Math.max(1, Math.floor((rLast - rFirst) / (rect.height * dpr * 2)));
    // when samples are far apart, join them with a smooth curve rather than
    // straight segments — a monotone (Steffen) cubic of the *clipped*
    // samples, which never overshoots its neighbours, so it can't invent
    // peaks / side lobes (e.g. around a spike) that aren't in the data
    const sub = rStep === 1 ? Math.min(16, Math.max(1, Math.ceil(rowPx / 3))) : 1;
    const data = samples.data;
    const clampE = (e: number) => Math.max(-maxExcursion, Math.min(maxExcursion, e));

    const fill = new Path2D();
    const line = new Path2D();
    const ys: number[] = [];
    const es: number[] = [];
    for (let j = jFirst; j <= jLast; j += stride) {
      const x0 = (j + 0.5 - c0) * colPx;
      const base = j * nRows;
      const amp = (r: number) =>
        clampE(data[base + Math.max(0, Math.min(nRows - 1, r))] * pxPerAmp);

      // the curve as (y, excursion) points
      ys.length = 0;
      es.length = 0;
      for (let r = rFirst; r <= rLast; r += rStep) {
        const y = (r + 0.5 - r0) * rowPx;
        if (sub === 1 || r === rLast) {
          ys.push(y);
          es.push(amp(r));
          continue;
        }
        const p0 = amp(r - 1);
        const p1 = amp(r);
        const p2 = amp(r + 1);
        const p3 = amp(r + 2);
        const m1 = steffenSlope(p0, p1, p2);
        const m2 = steffenSlope(p1, p2, p3);
        for (let k = 0; k < sub; k++) {
          const t = k / sub;
          const t2 = t * t;
          const t3 = t2 * t;
          // cubic Hermite between samples r and r+1
          const e =
            (2 * t3 - 3 * t2 + 1) * p1 +
            (t3 - 2 * t2 + t) * m1 +
            (-2 * t3 + 3 * t2) * p2 +
            (t3 - t2) * m2;
          ys.push(y + t * rowPx);
          es.push(e);
        }
      }
      if (!ys.length) continue;

      line.moveTo(x0 + es[0], ys[0]);
      for (let i = 1; i < ys.length; i++) line.lineTo(x0 + es[i], ys[i]);

      // positive lobes, cut exactly at the zero crossings
      fill.moveTo(x0, ys[0]);
      fill.lineTo(x0 + Math.max(0, es[0]), ys[0]);
      for (let i = 1; i < ys.length; i++) {
        const ea = es[i - 1];
        const eb = es[i];
        if ((ea > 0) !== (eb > 0) && ea !== eb) {
          const yc = ys[i - 1] + ((ys[i] - ys[i - 1]) * ea) / (ea - eb);
          fill.lineTo(x0, yc);
        }
        fill.lineTo(x0 + Math.max(0, eb), ys[i]);
      }
      fill.lineTo(x0, ys[ys.length - 1]);
      fill.closePath();
    }
    ctx.fillStyle = "#000";
    ctx.fill(fill);
    ctx.strokeStyle = "#000";
    ctx.lineWidth = Math.min(1, Math.max(0.5, spacing / 8));
    ctx.lineJoin = "round";
    ctx.stroke(line);
  }

  /** Resample the cells in view onto the canvas's device pixels with a box
   * filter: each pixel is the area-weighted average of the cells it covers.
   * Squeezed cells blend instead of aliasing (nearest/bilinear scaling skips
   * samples), magnified ones keep sharp edges with no gaps between them.
   * Separable: down the traces first, then across them. */
  private drawCells(
    ctx: CanvasRenderingContext2D,
    el: HTMLCanvasElement,
    greys: Float32Array,
    nCols: number,
    nRows: number,
    c0: number,
    c1: number,
    r0: number,
    r1: number,
  ): void {
    const W = el.width;
    const H = el.height;
    if (W < 1 || H < 1) return;
    const rw = areaWeights(r0, r1, H, nRows);
    const cw = areaWeights(c0, c1, W, nCols);
    const jA = Math.max(0, Math.floor(c0));
    const jB = Math.min(nCols, Math.ceil(c1));
    const BG = 255; // outside the gather: white, as the plot background

    // pass 1: each visible trace resampled to H pixels down
    const cols = new Float32Array(Math.max(0, jB - jA) * H);
    for (let j = jA; j < jB; j++) {
      const src = j * nRows;
      const dst = (j - jA) * H;
      for (let y = 0; y < H; y++) {
        let v = 0;
        let wsum = 0;
        for (let e = rw.start[y]; e < rw.start[y + 1]; e++) {
          v += rw.w[e] * greys[src + rw.k[e]];
          wsum += rw.w[e];
        }
        cols[dst + y] = v + (1 - wsum) * BG;
      }
    }

    // pass 2: across the traces, into the canvas pixels
    const image = ctx.createImageData(W, H);
    const out = image.data;
    for (let x = 0; x < W; x++) {
      const e0 = cw.start[x];
      const e1 = cw.start[x + 1];
      let wsum = 0;
      for (let e = e0; e < e1; e++) wsum += cw.w[e];
      const bg = (1 - wsum) * BG;
      for (let y = 0; y < H; y++) {
        let v = bg;
        for (let e = e0; e < e1; e++) {
          v += cw.w[e] * cols[(cw.k[e] - jA) * H + y];
        }
        const o = (y * W + x) * 4;
        out[o] = out[o + 1] = out[o + 2] = v;
        out[o + 3] = 255;
      }
    }
    ctx.putImageData(image, 0, 0);
  }

  /** Cursor readout: a faint line through the tracks at the hovered trace and
   * each track's value there (or at the selected trace when not hovering). */
  private drawHover(): void {
    const svgEl = this.overlaySvg()?.nativeElement;
    if (!svgEl) return;
    const svg = d3.select(svgEl);
    svg.selectAll("*").remove();

    const tracks = this.headerTracks();
    const meta = this.meta();
    const rect = this.plotRect();
    const dom = this.viewDomain();
    const hover = this.hoverTrace();
    const trace = hover ?? this.selectedTraceIndex();
    if (!tracks.length || !meta || !rect || !dom || trace == null) return;

    const xScale = d3
      .scaleLinear()
      .domain([dom.traceLo, dom.traceHi])
      .range([0, rect.width]);
    const g = svg
      .append("g")
      .attr("transform", `translate(${rect.left}, ${rect.top})`);

    if (hover != null) {
      g.append("line")
        .attr("x1", xScale(hover + 0.5))
        .attr("x2", xScale(hover + 0.5))
        .attr("y1", -this.tracksHeight())
        .attr("y2", 0)
        .attr("stroke", "rgba(0,0,0,0.35)")
        .attr("stroke-width", 1);
    }

    tracks.forEach((track, k) => {
      const top = -this.tracksHeight() + k * (TRACK_HEIGHT + TRACK_GAP);
      const idx = Math.floor((trace * track.values.length) / meta.n_traces);
      const value = track.values[idx];
      if (value == null) return;
      g.append("text")
        .attr("x", rect.width - 6)
        .attr("y", top + 11)
        .attr("text-anchor", "end")
        .attr("fill", "#333")
        .style("font-size", "10px")
        .style("font-variant-numeric", "tabular-nums")
        .text(`${this.formatValue(value)}  · trace ${trace + 1}`);
    });
  }

  // ─────────────────────────────────────────────────────────────
  // FORMATTING
  // ─────────────────────────────────────────────────────────────

  /** Compact track tick labels (coordinates run into the millions). */
  private formatTick(value: number): string {
    return Math.abs(value) >= 1e4 ? d3.format("~s")(value) : d3.format("~g")(value);
  }

  private formatValue(value: number): string {
    return Number.isInteger(value) ? `${value}` : `${+value.toFixed(3)}`;
  }

  /** Compact amplitude formatting for the caption (seismic values span
   * many orders of magnitude, so fall back to exponential notation). */
  protected formatAmp(value: number | null | undefined): string {
    if (value == null || !isFinite(value)) return "-";
    if (value === 0) return "0";
    const abs = Math.abs(value);
    if (abs >= 1000 || abs < 0.01) return value.toExponential(2);
    return Number(value.toPrecision(3)).toString();
  }
}
